import { useHistoricoDeTransicoes } from '../../../api/queries/gmuds'
import { AvisoDeErro, Carregando } from '../../../components/Estados'
import { IconeAlerta } from '../../../components/Icones'
import { Cartao } from '../../../components/ListaDeDados'
import { cn } from '../../../lib/cn'
import { dataHora, dataHoraPorExtenso } from '../../../lib/datas'
import { ROTULO_STATUS, TEXTOS } from '../../../lib/glossario'
import type { OrigemGmud, RegistroTransicao } from '../../../types/gmud'

/**
 * A trilha de mudanças de estado, do começo ao fim.
 *
 * Quem executou vem nulo quando a mudança NÃO passou pela aplicação — `UPDATE` direto no psql,
 * script de correção. O comentário da coluna, na V5, diz o que isso é: "sinal de auditoria, não
 * erro", e a linha mais importante que a tabela pode ter. Por isso ela é destacada em vez de
 * escondida, e vem com a explicação do que significa: quem lê esta tela é a fiscalização, e uma
 * linha sem autor não diz nada sozinha para quem não conhece o schema.
 *
 * @param props identificador e procedência da solicitação
 * @returns o cartão do histórico
 */
export function LinhaDoTempo({ gmudId, origem }: { gmudId: number; origem: OrigemGmud }) {
  const { data: registros, isPending, isError, error } = useHistoricoDeTransicoes(gmudId)

  return (
    <Cartao
      titulo="Histórico"
      descricao="Toda mudança de estado, na ordem em que aconteceu. A trilha é gravada pelo banco e não pode ser editada nem apagada."
    >
      {isPending && <Carregando />}
      {isError && <AvisoDeErro erro={error} titulo="Não foi possível carregar o histórico" />}
      {registros?.length === 0 && <p className="text-sm text-slate-500">{TEXTOS.semHistorico}</p>}
      {registros && registros.length > 0 && (
        <ol className="space-y-4 border-l border-slate-200 pl-4">
          {registros.map((registro) => (
            <Movimento key={registro.id} registro={registro} origem={origem} />
          ))}
        </ol>
      )}
      {origem === 'MIGRADO_V1' && <NotaDoSistemaAnterior />}
    </Cartao>
  )
}

/**
 * Se esta linha é uma mudança feita por fora da aplicação.
 *
 * A criação de um registro importado também chega sem autor, e não é a mesma coisa: o trigger
 * que registra a criação copia `criado_por`, que a V10 permite ser nulo justamente para o
 * histórico — não havia usuário deste sistema para responder por ela. Tratar as duas do mesmo
 * jeito acenderia o alerta de auditoria em 1.027 registros e o tornaria invisível no dia em que
 * ele importasse.
 *
 * @param registro a linha da trilha
 * @param origem a procedência da solicitação
 * @returns true quando a mudança não passou pela aplicação
 */
function foraDaAplicacao(registro: RegistroTransicao, origem: OrigemGmud): boolean {
  if (registro.executadoPor != null) {
    return false
  }
  return !(origem === 'MIGRADO_V1' && registro.statusDe == null)
}

function Movimento({ registro, origem }: { registro: RegistroTransicao; origem: OrigemGmud }) {
  const de = registro.statusDe ? (ROTULO_STATUS[registro.statusDe] ?? registro.statusDeRotulo) : null
  const para = ROTULO_STATUS[registro.statusPara] ?? registro.statusParaRotulo
  const fora = foraDaAplicacao(registro, origem)
  const importada = registro.executadoPor == null && !fora

  return (
    <li className="relative">
      <span
        aria-hidden="true"
        className={cn(
          'absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full',
          fora ? 'bg-amber-500 ring-2 ring-amber-200' : 'bg-niteroi-600',
        )}
      />
      <p className="text-sm font-medium text-slate-900">
        {de ? `${de} → ${para}` : `Criada como ${para}`}
        {registro.comandoRotulo && <span className="font-normal text-slate-600"> · {registro.comandoRotulo}</span>}
      </p>
      <p className="text-xs text-slate-500">
        <time dateTime={registro.ocorridoEm} title={dataHoraPorExtenso(registro.ocorridoEm)}>
          {dataHora(registro.ocorridoEm)}
        </time>
        {' · '}
        {registro.executadoPorNome ?? (importada ? 'Importação do sistema anterior' : 'Sem usuário identificado')}
      </p>
      {fora && <AlteracaoPorFora />}
      {registro.motivo && (
        <p className="mt-1 whitespace-pre-line rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
          {registro.motivo}
        </p>
      )}
    </li>
  )
}

/** O destaque da linha sem autor: o que ela quer dizer, para quem não conhece o banco. */
function AlteracaoPorFora() {
  return (
    <p className="mt-1.5 flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <IconeAlerta className="mt-px h-3.5 w-3.5 shrink-0 text-amber-700" />
      <span className="max-w-prose">
        <strong className="font-semibold">Alteração feita fora do sistema.</strong> Esta mudança de estado não passou
        pela aplicação — veio de um comando direto no banco, e por isso não tem autor. O registro existe de
        propósito: é assim que uma mudança que ninguém autorizou por aqui fica visível. Vale apurar quem fez e por
        quê.
      </span>
    </p>
  )
}

/**
 * O que esta aba ainda não mostra num registro importado.
 *
 * As movimentações do sistema anterior vieram na importação, sem tradução, e continuam guardadas
 * — traduzi-las para a trilha daqui exigiria cadastrar os estados de lá ("Aprovado",
 * "Analisando") entre os estados deste processo, que é exatamente a confusão que a V10 evitou.
 * Ainda não há tela nem endpoint para elas, e dizer isso é mais honesto do que deixar a aba
 * parecer que o registro nunca se moveu.
 *
 * @returns a nota do histórico importado
 */
function NotaDoSistemaAnterior() {
  return (
    <p className="mt-4 max-w-prose border-t border-slate-100 pt-4 text-xs text-slate-500">
      Este registro veio do sistema anterior, e o que se vê acima é só a importação dele. As movimentações que ele
      teve lá foram preservadas na íntegra e sem tradução, mas ainda não têm tela aqui: esta parte do sistema
      continua sendo construída.
    </p>
  )
}
